import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CustomSelect from '../CustomSelect';
import Modal from '../Modal';
import FormField from '../FormField';
import PhoneInput, {
  formatInternationalPhoneForStorage,
  parseInternationalPhoneFromStorage,
} from '../PhoneInput';
import {
  cancelAdminNetworkingBooking,
  createAdminNetworkingBooking,
  fetchAdminNetworkingBookings,
  fetchAdminNetworkingConfig,
  fetchAdminNetworkingSchedule,
  updateAdminNetworkingBooking,
  type AdminBookingPayload,
  type AdminBookingRow,
  type ManualContactInput,
} from '../../services/adminNetworkingApi';
import type { SlotRow } from '../networking/networkingApi';
import {
  emptyManualContact,
  isManualRowComplete,
  isManualRowPartial,
  ManualContactFieldsBlock,
  type ManualContactRow,
} from '../networking/PartnerManualFields';
import { UserIcon, PlusIcon, RefreshIcon } from '../icons/AdminIcons';
import './AdminNetworkingSection.css';

function fmtRange(startIso: string, endIso: string) {
  const a = new Date(startIso);
  const b = new Date(endIso);
  return `${a.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} – ${b.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}`;
}

function fmtDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' });
}

const SLOT_MATCH_MS = 120000;

function slotStartsMatch(a: string, b: string) {
  return Math.abs(new Date(a).getTime() - new Date(b).getTime()) < SLOT_MATCH_MS;
}

function manualFromBooking(b: AdminBookingRow): ManualContactRow[] {
  if (b.manual_contacts_json) {
    try {
      const arr = JSON.parse(b.manual_contacts_json) as ManualContactInput[];
      if (Array.isArray(arr) && arr.length) {
        return arr.map((c) => {
          const parsed = parseInternationalPhoneFromStorage(c.phone || '');
          return {
            fullName: c.fullName || '',
            email: c.email || '',
            organization: c.organization || '',
            codigoPais: parsed.codigoPais || 'AR',
            numeroTelefono: parsed.numeroTelefono || '',
          };
        });
      }
    } catch {
      /* ignore */
    }
  }
  if (b.manual_name || b.manual_company) {
    const parsed = parseInternationalPhoneFromStorage('');
    return [
      {
        fullName: b.manual_name || '',
        email: b.manual_email || '',
        organization: b.manual_company || '',
        codigoPais: parsed.codigoPais || 'AR',
        numeroTelefono: parsed.numeroTelefono || '',
      },
    ];
  }
  return [];
}

function counterpartLabel(b: AdminBookingRow): string {
  const pt = String(b.partner_type || '').toLowerCase();
  if (pt === 'none') return 'Sin definir';
  if (pt === 'manual') {
    const people = manualFromBooking(b);
    if (!people.length || !people[0].fullName) return 'Manual';
    const p = people[0];
    const extra = people.length > 1 ? ` (+${people.length - 1})` : '';
    return `${p.fullName} · ${p.organization}${extra}`;
  }
  return pt || '—';
}

interface AdminNetworkingSectionProps {
  isDarkMode?: boolean;
}

const AdminNetworkingSection: React.FC<AdminNetworkingSectionProps> = ({ isDarkMode }) => {
  const selectOptionsClass = isDarkMode ? 'custom-select-options-dark' : '';

  const [eventDates, setEventDates] = useState<string[]>([]);
  const [filterDate, setFilterDate] = useState('');
  const [filterSlotStart, setFilterSlotStart] = useState('');
  const [filterScheduleSlots, setFilterScheduleSlots] = useState<SlotRow[]>([]);
  const [bookings, setBookings] = useState<AdminBookingRow[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AdminBookingRow | null>(null);
  const [formDate, setFormDate] = useState('');
  const [formSlotStart, setFormSlotStart] = useState('');
  const [slots, setSlots] = useState<SlotRow[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [guestCompany, setGuestCompany] = useState('');
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhoneCountry, setGuestPhoneCountry] = useState('AR');
  const [guestPhoneNumber, setGuestPhoneNumber] = useState('');
  const [manualContacts, setManualContacts] = useState<ManualContactRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [cancelId, setCancelId] = useState<number | null>(null);

  const dateOptions = useMemo(
    () =>
      eventDates.map((d) => ({
        value: d,
        label: new Date(`${d}T12:00:00`).toLocaleDateString('es-AR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }),
      })),
    [eventDates]
  );

  const slotFilterOptions = useMemo(
    () => [
      { value: '', label: 'Todos los horarios' },
      ...filterScheduleSlots.map((s) => ({
        value: s.slotStart,
        label: fmtRange(s.slotStart, s.slotEnd),
      })),
    ],
    [filterScheduleSlots]
  );

  const filteredBookings = useMemo(() => {
    if (!filterSlotStart) return bookings;
    return bookings.filter((b) => slotStartsMatch(b.slot_start, filterSlotStart));
  }, [bookings, filterSlotStart]);

  const loadBookings = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const r = await fetchAdminNetworkingBookings(filterDate || undefined);
      setBookings(r.data || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar reuniones');
      setBookings([]);
    } finally {
      setLoadingList(false);
    }
  }, [filterDate]);

  useEffect(() => {
    fetchAdminNetworkingConfig()
      .then((r) => {
        const dates = r.data?.eventDates || [];
        setEventDates(dates);
        if (dates.length && !filterDate) {
          setFilterDate(dates[0]);
        }
      })
      .catch(() => setError('No se pudo cargar la configuración de networking'));
  }, []);

  useEffect(() => {
    if (filterDate) void loadBookings();
  }, [filterDate, loadBookings]);

  useEffect(() => {
    setFilterSlotStart('');
    if (!filterDate) {
      setFilterScheduleSlots([]);
      return;
    }
    let cancelled = false;
    fetchAdminNetworkingSchedule(filterDate)
      .then((r) => {
        if (!cancelled) setFilterScheduleSlots(r.data || []);
      })
      .catch(() => {
        if (!cancelled) setFilterScheduleSlots([]);
      });
    return () => {
      cancelled = true;
    };
  }, [filterDate]);

  useEffect(() => {
    if (!modalOpen || !formDate || editing) return;
    let cancelled = false;
    setLoadingSlots(true);
    fetchAdminNetworkingSchedule(formDate)
      .then((r) => {
        if (!cancelled) setSlots(r.data || []);
      })
      .catch(() => {
        if (!cancelled) setSlots([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });
    return () => {
      cancelled = true;
    };
  }, [modalOpen, formDate, editing]);

  function openCreate() {
    setEditing(null);
    setFormDate(filterDate || eventDates[0] || '');
    setFormSlotStart('');
    setGuestCompany('');
    setGuestFirstName('');
    setGuestLastName('');
    setGuestEmail('');
    setGuestPhoneCountry('AR');
    setGuestPhoneNumber('');
    setManualContacts([emptyManualContact()]);
    setModalOpen(true);
  }

  function openEdit(b: AdminBookingRow) {
    setEditing(b);
    setFormDate(filterDate);
    setFormSlotStart(b.slot_start);
    setGuestCompany(b.guest_company || '');
    setGuestFirstName(b.guest_first_name || '');
    setGuestLastName(b.guest_last_name || '');
    setGuestEmail(b.guest_email || '');
    const ph = parseInternationalPhoneFromStorage(b.guest_phone || '');
    setGuestPhoneCountry(ph.codigoPais || 'AR');
    setGuestPhoneNumber(ph.numeroTelefono || '');
    const rows = manualFromBooking(b);
    setManualContacts(rows.length > 0 ? rows : [emptyManualContact()]);
    setModalOpen(true);
  }

  function buildPayload(): AdminBookingPayload {
    const phone = formatInternationalPhoneForStorage(guestPhoneCountry, guestPhoneNumber);
    const completeManualRows: ManualContactInput[] = manualContacts
      .filter(isManualRowComplete)
      .map((c) => ({
        fullName: c.fullName.trim(),
        email: c.email?.trim() || '',
        phone: formatInternationalPhoneForStorage(c.codigoPais, c.numeroTelefono),
        organization: c.organization.trim(),
      }));
    const effectivePartnerType = completeManualRows.length > 0 ? 'manual' : 'none';
    return {
      date: formDate,
      slotStart: formSlotStart,
      partnerType: effectivePartnerType,
      guestFirstName: guestFirstName.trim(),
      guestLastName: guestLastName.trim(),
      guestEmail: guestEmail.trim().toLowerCase(),
      guestPhone: phone,
      guestCompany: guestCompany.trim(),
      guestOrganizationType: '',
      manualContacts: effectivePartnerType === 'manual' ? completeManualRows : undefined,
    };
  }

  async function handleSave() {
    if (!guestCompany.trim()) {
      setError('Organización del reservador es obligatoria');
      return;
    }
    if (!guestFirstName.trim()) {
      setError('Nombre del reservador es obligatorio');
      return;
    }
    if (!guestLastName.trim()) {
      setError('Apellido del reservador es obligatorio');
      return;
    }
    const emailTrim = guestEmail.trim();
    if (emailTrim && !emailTrim.includes('@')) {
      setError('Si cargás correo del reservador, tiene que ser una dirección válida');
      return;
    }
    if (manualContacts.some(isManualRowPartial)) {
      setError(
        'Completá nombre completo, organización y un correo válido (si lo cargás) en cada contraparte, o quitá las filas incompletas.'
      );
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = buildPayload();
      if (editing) {
        const { date: _d, slotStart: _s, ...rest } = payload;
        await updateAdminNetworkingBooking(editing.id, rest);
        setSuccess('Reunión actualizada');
      } else {
        if (!formSlotStart) {
          setError('Elegí un horario');
          setSaving(false);
          return;
        }
        await createAdminNetworkingBooking(payload);
        setSuccess('Reunión creada');
      }
      setModalOpen(false);
      await loadBookings();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }

  async function handleCancelConfirm() {
    if (cancelId == null) return;
    setSaving(true);
    setError(null);
    try {
      await cancelAdminNetworkingBooking(cancelId);
      setSuccess('Reunión cancelada');
      setCancelId(null);
      await loadBookings();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cancelar');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="admin-net-section">
      <div className="admin-net-header">
        <h2 className="admin-net-title">
          <UserIcon width={22} height={22} /> Networking — reuniones
        </h2>
      </div>

      {error && (
        <div className="admin-net-alert admin-net-alert--error" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="admin-net-alert admin-net-alert--ok" role="status">
          {success}
          <button type="button" className="admin-net-alert-dismiss" onClick={() => setSuccess(null)}>
            ×
          </button>
        </div>
      )}

      <div className="admin-net-toolbar admin-crm-toolbar-row">
        <div className="admin-net-toolbar-field">
          <label className="info-label">Día</label>
          {eventDates.length ? (
            <CustomSelect
              className="admin-filter-select admin-toolbar-control"
              optionsClassName={selectOptionsClass}
              value={filterDate}
              onChange={setFilterDate}
              options={dateOptions}
            />
          ) : (
            <input
              type="date"
              className="form-input admin-filter-select admin-toolbar-control"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
            />
          )}
        </div>
        <div className="admin-net-toolbar-field">
          <label className="info-label">Horario</label>
          <CustomSelect
            className="admin-filter-select admin-toolbar-control"
            optionsClassName={selectOptionsClass}
            value={filterSlotStart}
            onChange={setFilterSlotStart}
            options={slotFilterOptions}
          />
        </div>
        <div className="admin-toolbar-actions">
          <button
            type="button"
            className="admin-toolbar-control admin-toolbar-btn-export"
            onClick={() => void loadBookings()}
            disabled={loadingList}
          >
            <RefreshIcon width={16} height={16} /> Actualizar
          </button>
          <button type="button" className="admin-toolbar-control admin-toolbar-btn-create" onClick={openCreate}>
            <PlusIcon width={16} height={16} /> Nueva reunión
          </button>
        </div>
      </div>

      {loadingList ? (
        <p className="admin-net-muted">Cargando reuniones…</p>
      ) : (
        <div className="admin-net-table-wrap">
          <table className="admin-net-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Horario</th>
                <th>Reservador</th>
                <th>Organización</th>
                <th>Email</th>
                <th>Contraparte</th>
                <th>Origen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredBookings.length === 0 ? (
                <tr>
                  <td colSpan={8} className="admin-net-empty">
                    {filterSlotStart
                      ? 'No hay reuniones para este horario.'
                      : 'No hay reuniones para este día.'}
                  </td>
                </tr>
              ) : (
                filteredBookings.map((b) => (
                  <tr key={b.id}>
                    <td>{fmtDate(b.slot_start)}</td>
                    <td>{fmtRange(b.slot_start, b.slot_end)}</td>
                    <td>
                      {b.guest_first_name} {b.guest_last_name}
                    </td>
                    <td>{b.guest_company}</td>
                    <td>{b.guest_email || '—'}</td>
                    <td>{counterpartLabel(b)}</td>
                    <td>{b.user_id ? 'Perfil' : 'Admin'}</td>
                    <td className="admin-net-actions">
                      <button type="button" className="admin-action-btn-small" onClick={() => openEdit(b)}>
                        Editar
                      </button>
                      <button
                        type="button"
                        className="admin-action-btn-small admin-net-btn-cancel"
                        onClick={() => setCancelId(b.id)}
                      >
                        Cancelar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        closeOnBackdropClick={false}
        title={editing ? 'Editar reunión' : 'Nueva reunión'}
        contentClassName={isDarkMode ? 'admin-panel dark-theme' : ''}
      >
        <div className="admin-net-form">
          {!editing && (
            <>
              <div className="admin-net-form-row">
                <label className="info-label">Día</label>
                {eventDates.length ? (
                  <CustomSelect
                    className="admin-filter-select"
                    optionsClassName={selectOptionsClass}
                    value={formDate}
                    onChange={setFormDate}
                    options={dateOptions}
                  />
                ) : (
                  <input
                    type="date"
                    className="form-input"
                    value={formDate}
                    onChange={(e) => setFormDate(e.target.value)}
                  />
                )}
              </div>
              <div className="admin-net-form-row">
                <label className="info-label">Horario</label>
                {loadingSlots && <p className="admin-net-muted">Cargando horarios…</p>}
                <div className="admin-net-slots">
                  {slots.map((s) => (
                    <button
                      key={s.slotStart}
                      type="button"
                      disabled={s.booked}
                      className={`admin-net-slot ${formSlotStart === s.slotStart ? 'admin-net-slot--on' : ''} ${s.booked ? 'admin-net-slot--busy' : ''}`}
                      onClick={() => setFormSlotStart(s.slotStart)}
                    >
                      {fmtRange(s.slotStart, s.slotEnd)}
                      {s.booked ? ' (ocupado)' : ''}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          {editing && (
            <p className="admin-net-muted">
              Horario: {fmtDate(editing.slot_start)} · {fmtRange(editing.slot_start, editing.slot_end)}
            </p>
          )}

          <h4 className="admin-net-form-subtitle">Reservador</h4>
          <div className="admin-net-form-grid admin-net-form-grid--guest">
            <FormField label="Organización" required>
              <input
                className="form-input"
                type="text"
                autoComplete="organization"
                value={guestCompany}
                onChange={(e) => setGuestCompany(e.target.value)}
              />
            </FormField>
            <FormField label="Nombre" required>
              <input
                className="form-input"
                type="text"
                autoComplete="given-name"
                value={guestFirstName}
                onChange={(e) => setGuestFirstName(e.target.value)}
              />
            </FormField>
            <FormField label="Apellido" required>
              <input
                className="form-input"
                type="text"
                autoComplete="family-name"
                value={guestLastName}
                onChange={(e) => setGuestLastName(e.target.value)}
              />
            </FormField>
            <FormField label="Correo electrónico">
              <input
                className="form-input"
                type="email"
                autoComplete="email"
                inputMode="email"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
              />
            </FormField>
            <FormField label="Teléfono" className="admin-net-field-full">
              <PhoneInput
                codigoPais={guestPhoneCountry}
                numeroTelefono={guestPhoneNumber}
                onCodigoPaisChange={setGuestPhoneCountry}
                onNumeroTelefonoChange={setGuestPhoneNumber}
              />
            </FormField>
          </div>

          <h4 className="admin-net-form-subtitle">Contraparte</h4>
          <div className="admin-net-contraparte">
            <ManualContactFieldsBlock
              contacts={manualContacts}
              setContacts={setManualContacts}
              fieldIdPrefix="admin-net-manual"
              maxContacts={null}
              showAddButtonWhenEmpty={false}
              minContacts={1}
              compactPhone={false}
              addButtonClassName="admin-net-add-partner-btn"
            />
          </div>

          <div className="admin-net-form-actions">
            <button
              type="button"
              className="admin-toolbar-control admin-toolbar-btn-export"
              onClick={() => setModalOpen(false)}
            >
              Cerrar
            </button>
            <button
              type="button"
              className="admin-toolbar-control admin-toolbar-btn-create"
              disabled={saving}
              onClick={() => void handleSave()}
            >
              {saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear reunión'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={cancelId != null}
        onClose={() => setCancelId(null)}
        closeOnBackdropClick={false}
        title="Cancelar reunión"
        contentClassName={isDarkMode ? 'admin-panel dark-theme' : ''}
      >
        <p>¿Cancelar esta reunión? Se marcará como cancelada en Google Sheets.</p>
        <div className="admin-net-form-actions">
          <button
            type="button"
            className="admin-toolbar-control admin-toolbar-btn-export"
            onClick={() => setCancelId(null)}
          >
            Volver
          </button>
          <button
            type="button"
            className="admin-toolbar-control admin-toolbar-btn-create"
            disabled={saving}
            onClick={() => void handleCancelConfirm()}
          >
            Confirmar cancelación
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AdminNetworkingSection;
